import { styled } from '@compiled/react';
import React, { useEffect, useState, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports
import ReactDOM from 'react-dom';
import { defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { ConfluenceEdition } from '@confluence/change-edition';
import { getLegacyMessageQueue } from '@confluence/legacy-message-queue';
import { useSessionData } from '@confluence/session-data';

import type {
	CopySpacePermissionsSelection,
	CopySpacePermissionsSourceSpace,
} from './CopySpacePermissionsSelector';
import { CopySpacePermissionsSelector, isSourceSpaceValid } from './CopySpacePermissionsSelector';
import { getOptionsByEdition, Option } from './Option';

const NEW_SPACE_LEGACY_DIALOG_ID = 'create-dialog';
const NEW_SPACE_LEGACY_MAKE_PRIVATE_CHECKBOX_ID = 'permission-private';
const NEW_SPACE_LEGACY_PERMISSIONS_SUBFORM_ID = 'space-form-permissions';
const NEW_SPACE_LEGACY_TEAM_DESCRIPTION_ID = 'team-description';
const NEW_SPACE_LEGACY_TEXTAREA_CSS_SELECTOR = `#${NEW_SPACE_LEGACY_DIALOG_ID} textarea`;
const NEW_SPACE_PERMISSIONS_ALL_FIELDS_VALID_ID = 'copy-space-permissions-fields-valid';
const NEW_SPACE_PERMISSIONS_SOURCE_FIELD_ID = 'copy-space-permissions-key-for-create';
const NEW_SPACE_PERMISSIONS_PORTAL_ID = 'new-space-permissions-portal-container';
const TEAM_SPACE_TEMPLATE_TEAM_MEMBERS_ID = 'team-members';

const CHANGE_LISTENER_CSS_CLASSNAME = 'portal-change-listener';

const NEW_SPACE_LEGACY_TEAM_DESCRIPTION_MAX_ROWS = 2;

const i18n = defineMessages({
	optionSelectLabel: {
		id: 'copy-space-permissions.for-new-space.option-select.label',
		defaultMessage: 'Space permissions',
		description:
			'The label for the dropdown where the user to selects which set of permissions to use for a new space: default, private, etc.',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CopySpacePermissionsPanel = styled.div({
	marginTop: token('space.025', '2px'),
	position: 'absolute',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CopySpacePermissionsSubpanel = styled.div({
	position: 'fixed',
});

/* CSS Overrides for the Team Space Templates.  These templates are plugins that are stored in several
    other repositories so it is simpler to just override them here in one place */
const SpaceTemplateCssOverride = () => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Ignored via go/DSP-18766
	<style>
		{`
      /********************************************************************/
      /* Begin section for Copy Space Permissions on Create Space Dialog  */
      /********************************************************************/

      #${NEW_SPACE_LEGACY_DIALOG_ID} .dialog-wizard-page-main {
        padding-top: 10px;
      }

      #${NEW_SPACE_LEGACY_DIALOG_ID} .dialog-wizard-page-description {
        padding-top: 10px;
      }

      #${NEW_SPACE_LEGACY_DIALOG_ID} .error {
        margin-top: 3px;
      }

      /* Force the Team Space Template's Team Member field to have maximum height of 2 rows.
        The unmodified version of this field has unlimited height, which gets ugly especially
        with the additional Copy Space Permissions now below it. */
      #${NEW_SPACE_LEGACY_DIALOG_ID} ul.select2-choices {
        max-height: 54px;
        overflow: auto;
      }
   `}
	</style>
);

const getObjectConstructorName = (object: Object | null) => object?.constructor?.name;

export const CopySpacePermissionsForLegacyNewSpace = () => {
	/* Keep track of whether the Create New Space wizard has been displayed yet. This is done for
    efficiency, so we don't have to search the DOM for the portal target if that wizard hasn't been
    shown yet.  However, once the wizard is exited this state does not get reset until the page is
    refreshed */
	const [targetWizardActivated, setTargetWizardActivated] = useState(false);
	const [, setLastRefresh] = useState(0);

	const forceUpdate = () => {
		setLastRefresh(Date.now());
	};

	/* Passes the source Space key to the server-side rendered (SSR) part of the page by changing
    the value of a hidden field on the SSR side, which the SSR Javascript code will read and add
    to the Create New Space request to the backend */
	const setCreateSpaceSourceKeyInSSR = (sourceSpaceKey: string) => {
		const sourceField = document.getElementById(NEW_SPACE_PERMISSIONS_SOURCE_FIELD_ID);
		if (getObjectConstructorName(sourceField) === 'HTMLInputElement') {
			const sourceInputField = sourceField as HTMLInputElement;
			sourceInputField.value = sourceSpaceKey;
		}
	};

	/* Updates the hidden "copy-space-permissions-fields-valid" field on the legacy (SSR) side of
    the page to indicate whether the React-based controls have valid settings */
	const setReactFieldsValidInSSR = (
		option: Option | undefined,
		sourceSpace: CopySpacePermissionsSourceSpace,
	) => {
		const legacySpacePermissionsValidField = document.getElementById(
			NEW_SPACE_PERMISSIONS_ALL_FIELDS_VALID_ID,
		);
		if (getObjectConstructorName(legacySpacePermissionsValidField) === 'HTMLInputElement') {
			// Set the value in the SSR field
			// (Any non-empty string value means "valid")
			const inputField = legacySpacePermissionsValidField as HTMLInputElement;
			inputField.value = option && isSourceSpaceValid({ option, sourceSpace }) ? 'true' : '';

			// Tell the legacy UI code that the validity field has changed
			// (Note that while IE 11 doesn't support CustomEvent constructors, our main webpack config has the "events-polyfill" included)
			inputField.dispatchEvent(new CustomEvent('change', { bubbles: true }));
		}
	};

	/* We have to hide the "permission-private" checkbox and its' label, etc. every time we render our new UI
    because when this component's render is called we know that the SSR target has re-rendered and made the
    checkbox is visible again */
	const hideCreateSpaceOldPermissions = () => {
		const oldPermissions = document.getElementById(NEW_SPACE_LEGACY_PERMISSIONS_SUBFORM_ID);
		if (oldPermissions) {
			oldPermissions.classList.add('hidden');
		}
	};

	/* The different Space templates' SSR wizard pages may have a "description" or other textarea
    controls on them that by default are resizable - but because our React component will float
    over the SSR wizard page (so our dropdowns don't get cut off by "overflow: hidden") the
    textareas could be resized underneath our React component.  This method finds any such
    textareas and makes them fixed-size - which is okay because they will still scroll if the
    user types too much in them */
	const lockCreateSpaceTextareasSize = () => {
		document.querySelectorAll(NEW_SPACE_LEGACY_TEXTAREA_CSS_SELECTOR).forEach((element) => {
			if (getObjectConstructorName(element) === 'HTMLTextAreaElement') {
				const textareaElement = element as HTMLTextAreaElement;
				textareaElement.style.resize = 'none';
			}
		});
	};

	/* On SSR wizard page for the Team Space template, when the user chooses to add a team member
    to the template the control for the team members grows and then shrinks back again. Our
    portal gets re-rendered when the it the team members control grows (and our fields are psuhed
    down) but it is not re-rendered when it shrinks again.  This leaves a gap between the existing
    fields and our fields, and may also push our fields off the bottom of the dialog (especially
    if there are error messages visible).  We can hook into the events generated by the team
    member control to force a re-render when the selection is done. */
	/* Note that the team member control is a very old AUI component "user-group-select2" component
    found in the "confluence-ui-components" repo, so modifying the control itself to not expand-
    then-shrink seems risky.  And while this component is built on top of "Select2", which is an
    old jQuery replacement for the standard HTML <select> control.  But as the AUI user group
    control doesn't user all of the Select control functionally (the list of names isn't actually
    in the Select2 control's dropdown; AUI has created a separate div for that), we can't listen
    for the standard "select2-selected" event.  Instead we listen for when the contents of the
    controls changes */
	const renderOnTeamTemplateMembersChange = () => {
		// Use an empty CSS class as a flag to indicate whether we've already connected the listener
		const teamMembersControl = document.getElementById(
			TEAM_SPACE_TEMPLATE_TEAM_MEMBERS_ID,
		) as HTMLInputElement;
		if (
			teamMembersControl &&
			!teamMembersControl.classList.contains(CHANGE_LISTENER_CSS_CLASSNAME)
		) {
			teamMembersControl.classList.add(CHANGE_LISTENER_CSS_CLASSNAME);
			teamMembersControl.onchange = () => forceUpdate();
		}
	};

	/* We also need to adjust the Team Space Template's "Description" field's height or the portal
    may get rendered past the end of the dialog */
	const adjustTeamTemplateDescriptionSize = () => {
		const teamTemplateDesciption = document.getElementById(NEW_SPACE_LEGACY_TEAM_DESCRIPTION_ID);
		if (getObjectConstructorName(teamTemplateDesciption) === 'HTMLTextAreaElement') {
			const teamDescriptionTextarea = teamTemplateDesciption as HTMLTextAreaElement;
			teamDescriptionTextarea.rows = NEW_SPACE_LEGACY_TEAM_DESCRIPTION_MAX_ROWS;
		}
	};

	/* Move the portal target to be the last sibling in its' parent's list of children.  This is
    a hack to make components rendered into the portal appear after any other child elements,
    as some of the Space templates call the "createSpaceFormFields" template (which contains
    our portal target) and then add their extra fields after that - but our UI designers want
    our new added components to be last in the form.  And since most of the Space templates
    are plugins in other repos (and others may be added at any time) we have no easy way to
    add our portal to all of them. Therefore at runtime we'll just make sure our portal
    target is after everything else in the parent form */
	const makePortalTargetLastSibling = () => {
		const portalTarget = document.getElementById(NEW_SPACE_PERMISSIONS_PORTAL_ID);
		if (portalTarget) {
			const parent = portalTarget.parentElement;
			if (parent) {
				parent.appendChild(portalTarget);
			}
		}
	};

	/* Passes the "make private" flag to the server-side rendered (SSR) part of the page by changing
    the value of a hidden checkbox on the SSR side, which the SSR Javascript code will read and add
    to the Create New Space request to the backend */
	const setMakePrivateInSSR = (makePrivate) => {
		const privateCheckbox = document.getElementById(NEW_SPACE_LEGACY_MAKE_PRIVATE_CHECKBOX_ID);
		if (getObjectConstructorName(privateCheckbox) === 'HTMLInputElement') {
			const privateCheckboxInput = privateCheckbox as HTMLInputElement;
			privateCheckboxInput.checked = makePrivate;
		}
	};

	const onChange = ({ option, sourceSpace }: CopySpacePermissionsSelection) => {
		/* When the "make private" flag changes, pass the flag to its' hidden field on the SSR side of the page */
		setMakePrivateInSSR(option === Option.ONLY_VISIBLE_TO_ME);

		/* Because we are passing the selected source Space's key to the SSR side of the page in a
    hidden field on the SSR side, we need to copy the current setting to the SSR field when
    copying is selected and make sure to clear the SSR field's value when deselected */
		setCreateSpaceSourceKeyInSSR(sourceSpace.key);

		// Update the enable state of legacy UI's "Create" button based the validity of our fields
		setReactFieldsValidInSSR(option, sourceSpace);
	};

	const newSpaceWizardMessageHandler = useCallback(() => {
		/* If we haven't already seen the portal target yet, check if this is a wizard page with
      our portal target on it */
		if (!targetWizardActivated && document.getElementById(NEW_SPACE_PERMISSIONS_PORTAL_ID)) {
			setTargetWizardActivated(true);
		}

		// Always force a re-render, as React won't recognize when the SSR part of the page redraws
		forceUpdate();
	}, [targetWizardActivated]);

	// Customize the legacy SSR after every render for our new UI elements, if our portal target is visible
	useEffect(() => {
		if (targetWizardActivated && document.getElementById(NEW_SPACE_PERMISSIONS_PORTAL_ID)) {
			hideCreateSpaceOldPermissions();
			makePortalTargetLastSibling();
			lockCreateSpaceTextareasSize();
			adjustTeamTemplateDescriptionSize();
			renderOnTeamTemplateMembersChange();
		}
	});

	/* The Create New Space wizard is rendered dynamically on the browser at runtime (even though
    the Javascript code to do so is in the server-rendered pages) and that means we won't find
    the portal target on this page during the initial React render.  And since the wizard is
    plain Javascript, the React component tree does not get notified or re-rendered when the
    wizard is displayed.  So we need to have the wizard tell us when it has been rendered via
    the legacy-message-queue. This hook sets up the message handler for that event, so it only
    needs to be called once, at initialization time. But added newSpaceWizardMessageHandler to
    dependency array for eslint purposes */
	/* (Note that this component needs to in the DOM tree in classic:SPA pages, next:SPA pages
    and SSR pages, since there are "Create new space" buttons on the "Space Directory" page
    which is an SSR page, and on the "Activity" ("/discover" route) page which is a SPA page,
    and new "Home" page ("/wiki" route) */
	useEffect(() => {
		// set the callback for when the Create New Space wizard is rendered
		void getLegacyMessageQueue().then((legacyMessageQueue) => {
			legacyMessageQueue.push({
				type: 'ATLASSIAN_CONFLUENCE_NEW_SPACE_REGISTER_CALLBACK',
				payload: newSpaceWizardMessageHandler,
			});
		});
	}, [newSpaceWizardMessageHandler]);

	const { edition } = useSessionData();

	if (targetWizardActivated) {
		const portalTarget = document.getElementById(NEW_SPACE_PERMISSIONS_PORTAL_ID);
		if (portalTarget) {
			return ReactDOM.createPortal(
				edition ? (
					<CopySpacePermissionsPanel>
						<CopySpacePermissionsSubpanel>
							<CopySpacePermissionsSelector
								analyticsEventSourceName="createSpaceModal"
								defaultOption={Option.DEFAULT_PERMISSIONS}
								isDisabled={edition === ConfluenceEdition.FREE}
								label={i18n.optionSelectLabel}
								onChange={onChange}
								options={getOptionsByEdition(edition)}
								showUpsell={edition === ConfluenceEdition.STANDARD}
								renderGuestsWarning
								validationPlaceholder
							/>
						</CopySpacePermissionsSubpanel>
						<SpaceTemplateCssOverride />
					</CopySpacePermissionsPanel>
				) : null,
				portalTarget,
			);
		}
	}

	// If we couldn't render into the portal target or didn't need to, then don't render anything
	return null;
};
